import {
  Box3,
  Color,
  Object3D,
  OrthographicCamera,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import {Line2} from 'three/examples/jsm/lines/Line2.js';
import {LineGeometry} from 'three/examples/jsm/lines/LineGeometry.js';
import {LineMaterial} from 'three/examples/jsm/lines/LineMaterial.js';

import {
  checkArgument,
  checkExhaustive,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {CorgiEvent} from 'external/dev_april_corgi+/js/corgi/events';
import {CHANGED} from 'external/dev_april_corgi+/js/emu/events';
import {DialogService} from 'external/dev_april_corgi+/js/emu/dialog';

import {
  Graph,
  Link,
  Node,
  Pad,
  Patch,
  arePadsCompatible,
  hashMaterial,
  isPadAndSpecCompatible,
} from './execution/graph';
import {PLATE_IDS, plateIdToLabel} from './execution/lab_state';
import {NoOpActionsHandler} from './execution/no_op_actions_handler';

import {settle} from './execution/settling';
import {NODE_SPECS} from './nodes/nodes';
import {ready as starlarkReady} from './nodes/starlark';
import {RgbaU32} from './types/colors';
import {
  ConcentrationValue,
  DurationValue,
  MaterialId,
  MaterialValue,
  PlateId,
  ScriptValue,
  TableValue,
  VolumeValue,
} from './types/data';
import {Concentration, getInventory} from './types/custom';
import {
  DURATION_IDS,
  DURATION_LABELS,
  VOLUME_IDS,
  VOLUME_LABELS,
  DurationUnit,
  VolumeUnit,
  canonicalizeUnit,
  nlToUnit,
  unitToNanos,
  usToUnit,
} from './types/units';

import {makeAddSubgraphDialog} from './add_subgraph_dialog';
import {GRAPH_DIRTIED} from './events';
import {removeAndDispose} from './layout';
import {Divider, Menu, MenuItem} from './menu_controller';
import {MenuService} from './menu_service';
import {makeNodeEditor} from './node_editor_element';
import {makeNodeDetail, makePadTooltip} from './node_element';
import {PointerInterpreter} from './pointer_interpreter';
import {fireAndForget} from './promises';
import {
  ObjectHandle,
  PadHandle,
  SceneGraph,
  TYPE_IGNORE,
  TYPE_LINK,
  TYPE_NODE,
  TYPE_PAD,
} from './scene_graph';
import {makeScriptEditor} from './script_editor_element';
import {Spline} from './shaders';
import {makeSimulateScriptDialog} from './simulation_element';
import {makeTableEditor} from './table_editor_element';
import {capitalize} from './text_renderer';
import {Z_HOVER, Z_LINK} from './z';

interface Args {
  addMenuItems: Array<Menu | MenuItem>;
}

interface CameraState {
  version: number;
  x: number;
  y: number;
  zoom: number;
}

export interface State {
  input:
    | {
        position: Vector2;
        size: Vector2;
        value: string | undefined;
        scale: number;
      }
    | undefined;
  padInfo:
    | {
        target: PadHandle<'input' | 'output'>;
        position: Vector2;
        content: corgi.VElementOrPrimitive;
      }
    | undefined;
}

type Deps = typeof GraphController.deps;

type TypedObjectHandle<K extends ObjectHandle['kind']> = ObjectHandle & {
  kind: K;
};

const AIMLESS_LINK_CLASS = 'cursor-crosshair';
const CLICK_PLANE = new Plane(new Vector3(0, 0, 1));

export class GraphController extends Controller<
  Args,
  Deps,
  HTMLElement,
  State
> {
  static deps() {
    return {
      services: {
        dialog: DialogService,
        menu: MenuService,
      },
    };
  }

  readonly graph: Graph;
  private readonly camera: OrthographicCamera;
  private readonly glRenderer: WebGLRenderer;
  private readonly pointerInterpreter: PointerInterpreter;
  private readonly dialog: DialogService;
  private readonly menu: MenuService;
  private readonly menuItems: Array<Divider | Menu | MenuItem>;
  private readonly raycaster: Raycaster;
  private readonly scene: SceneGraph;
  private pendingDragSelect:
    | {
        boxGeometry: LineGeometry;
        boxVertices: Float32Array;
        boxObject: Line2;
        start: Vector3;
      }
    | undefined;
  private pendingLink:
    | {
        line: Spline;
        start: PadHandle<'input' | 'output'>;
        point: Vector3;
      }
    | undefined;
  private _zoom: number;

  constructor(response: Response<GraphController>) {
    super(response);
    this.dialog = response.deps.services.dialog;
    this.menu = response.deps.services.menu;
    this.menuItems = [
      {
        kind: 'menu',
        label: 'Add',
        items: response.args.addMenuItems,
      },
      {
        kind: 'menu_item',
        label: 'Simulate',
        action: () => fireAndForget(this.simulateRun()),
      },
    ];
    this.pendingDragSelect = undefined;
    this.pendingLink = undefined;
    this._zoom = 1;

    this.graph = new Graph();
    this.graph.addBehaviors(NODE_SPECS);
    this.graph.addListener({
      patch: async patches => {
        this.trigger(GRAPH_DIRTIED, {});
        await this.scene.patch(patches);
        const kinds = new Set(patches.map(p => p.kind));
        const cascade =
          kinds.has('add_node') ||
          kinds.has('delete_node') ||
          kinds.has('update_node');
        if (cascade) {
          await this.cascadeValues();
        }
      },
    });
    fireAndForget(starlarkReady().then(() => this.cascadeValues()));

    // TODO(april): this should be disposable but it's hard because it has WeakMaps and we don't
    // know what to clean up.
    this.scene = new SceneGraph(this.graph, {
      deleteLink: async (link: Link) => {
        await this.graph.deleteLink(link);
        await this.cascadeValues();
      },
      openDurationNumberEditor: (
        pad: Pad<'input'>,
        position: Vector3,
        bound: Box3,
      ) => {
        if (pad.type.kind !== 'duration') {
          throw new Error('Expected a duration');
        }

        const existing = (pad.unlinked?.value?.kind === 'duration'
          ? pad.unlinked.value
          : undefined) ?? {
          kind: 'duration',
          numberUs: 60 * 1000_000,
          preferredUnit: 's',
        };
        this.updateState({
          ...this.state,
          input: {
            position: this.calculatePx(position),
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            value: usToUnit(
              existing.numberUs,
              existing.preferredUnit,
            ).toString(),
            scale: Math.pow(2, this._zoom),
          },
        });
      },
      openDurationUnitEditor: (
        pad: Pad<'input'>,
        position: Vector3,
        bound: Box3,
      ) => {
        if (pad.type.kind !== 'duration') {
          throw new Error('Expected a duration');
        }

        const existing = (pad.unlinked?.value?.kind === 'duration'
          ? pad.unlinked.value
          : undefined) ?? {
          kind: 'duration',
          numberUs: 60 * 1000_000,
          preferredUnit: 's',
        };
        const initialUnit = existing.preferredUnit;
        const {accent, items} = optionsToFlatMenu(
          false,
          DURATION_IDS,
          DURATION_LABELS,
          initialUnit,
          value => {
            const current = checkExists(pad.unlinked?.value) as DurationValue;
            const after = checkExists(value) as DurationUnit;
            this.graph.setValue(pad, {
              kind: 'duration',
              numberUs:
                unitToNanos(
                  usToUnit(current.numberUs, current.preferredUnit),
                  after,
                ).number / 1000,
              preferredUnit: after,
            });
          },
        );
        this.openDropdown(items, accent, position, bound);
      },
      openFloatEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        const value = pad.unlinked?.value;
        this.updateState({
          ...this.state,
          input: {
            position: this.calculatePx(position),
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            value:
              value?.kind === 'float'
                ? value.preferredDisplay === 'percentage'
                  ? `${value.number * 100}%`
                  : String(value.number)
                : '',
            scale: Math.pow(2, this._zoom) / 2,
          },
        });
      },
      openIntEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        const value = pad.unlinked?.value;
        this.updateState({
          ...this.state,
          input: {
            position: this.calculatePx(position),
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            value: value?.kind === 'int' ? String(value.number) : '',
            scale: Math.pow(2, this._zoom) / 2,
          },
        });
      },
      openNodeEditor: (node: Node) => {
        this.openNodeEditor(node);
      },
      openPlateEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        if (pad.type.kind !== 'plate') {
          throw new Error('Expected a plate');
        }
        const value = pad.unlinked?.value;
        if (value && value.kind !== 'plate') {
          throw new Error('Expected a plate');
        }

        const options = [PLATE_IDS.eppendorfs, PLATE_IDS.fourCelsius];
        const labels = options.map(p => plateIdToLabel(p));
        const {accent, items} = optionsToFlatMenu(
          /* nullable= */ false,
          options,
          labels,
          pad.unlinked?.value?.kind === 'plate'
            ? pad.unlinked.value.id
            : undefined,
          value =>
            this.graph.setValue(
              pad,
              value
                ? {
                    kind: 'plate',
                    id: value as PlateId,
                    wells: new Map(),
                  }
                : undefined,
            ),
        );
        this.openDropdown(items, accent, position, bound);
      },
      openScriptEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        if (pad.type?.kind !== 'script') {
          throw new Error('Expected a script');
        }
        const value = pad.unlinked?.value ?? {kind: 'script', value: ''};
        if (value.kind !== 'script') {
          throw new Error('Expected a script');
        }
        const draft = {value};
        const onUpdate = (script: ScriptValue) => {
          draft.value = script;
        };
        const save = () => {
          return this.graph.setValue(pad, draft.value);
        };
        this.dialog
          .display(makeScriptEditor({type: pad.type, value, onUpdate}))
          // When the dialog closes by clickout the promise is rejected, but that's fine
          .then(save, save);
      },
      openSelectEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        if (pad.type.kind !== 'select') {
          throw new Error('Expected a select');
        }

        const {accent, items} = optionsToFlatMenu(
          pad.type.nullable,
          pad.type.options,
          pad.type.options.map(capitalize),
          pad.unlinked?.value?.kind === 'select'
            ? pad.unlinked.value.value
            : undefined,
          value =>
            this.graph.setValue(
              pad,
              value
                ? {
                    kind: 'select',
                    value,
                  }
                : undefined,
            ),
        );
        this.openDropdown(items, accent, position, bound);
      },
      openStringEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        const value = pad.unlinked?.value;
        this.updateState({
          ...this.state,
          input: {
            position: this.calculatePx(position),
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            value: value?.kind === 'string' ? value.value : '',
            scale: Math.pow(2, this._zoom) / 2,
          },
        });
      },
      openTableEditor: (pad: Pad<'input'>, position: Vector3, bound: Box3) => {
        if (pad.type?.kind !== 'table') {
          throw new Error('Expected a table');
        }
        const value = pad.unlinked?.value ?? {
          kind: 'table',
          values: new Map([['Label', []]]),
        };
        if (value?.kind !== 'table') {
          throw new Error('Expected a table');
        }
        const draft = {value};
        const onUpdate = (table: TableValue) => {
          draft.value = table;
        };
        const save = () => {
          return this.graph.setValue(pad, draft.value);
        };
        this.dialog
          .display(makeTableEditor({type: pad.type, value, onUpdate}))
          // When the dialog closes by clickout the promise is rejected, but that's fine
          .then(save, save);
      },
      openTemperatureEditor: (
        pad: Pad<'input'>,
        position: Vector3,
        bound: Box3,
      ) => {
        const value = pad.unlinked?.value;
        this.updateState({
          ...this.state,
          input: {
            position: this.calculatePx(position),
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            value: value?.kind === 'temperature' ? String(value.numberC) : '',
            scale: Math.pow(2, this._zoom) / 2,
          },
        });
      },
      openVolumeNumberEditor: (
        pad: Pad<'input'>,
        position: Vector3,
        bound: Box3,
      ) => {
        if (pad.type.kind !== 'volume') {
          throw new Error('Expected a volume');
        }

        const existing = (pad.unlinked?.value?.kind === 'volume'
          ? pad.unlinked.value
          : undefined) ?? {
          kind: 'volume',
          numberNl: 10_000,
          preferredUnit: 'ul',
        };
        this.updateState({
          ...this.state,
          input: {
            position: this.calculatePx(position),
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            value: nlToUnit(
              existing.numberNl,
              existing.preferredUnit,
            ).toString(),
            scale: Math.pow(2, this._zoom) / 2,
          },
        });
      },
      openVolumeUnitEditor: (
        pad: Pad<'input'>,
        position: Vector3,
        bound: Box3,
      ) => {
        if (pad.type.kind !== 'volume') {
          throw new Error('Expected a volume');
        }

        const existing = (pad.unlinked?.value?.kind === 'volume'
          ? pad.unlinked.value
          : undefined) ?? {
          kind: 'volume',
          numberNl: 10_000,
          preferredUnit: 'ul',
        };
        const initialUnit = existing.preferredUnit;
        const {accent, items} = optionsToFlatMenu(
          false,
          VOLUME_IDS,
          VOLUME_LABELS,
          initialUnit,
          value => {
            const current = checkExists(pad.unlinked?.value) as VolumeValue;
            const after = checkExists(value) as VolumeUnit;
            this.graph.setValue(pad, {
              kind: 'volume',
              numberNl: unitToNanos(
                nlToUnit(current.numberNl, current.preferredUnit),
                after,
              ).number,
              preferredUnit: after,
            });
          },
        );
        this.openDropdown(items, accent, position, bound);
      },
      openMaterialContentEditor: (
        pad: Pad<'input'>,
        position: Vector3,
        bound: Box3,
      ) => {
        if (pad.type.kind !== 'material') {
          throw new Error('Expected a material');
        }
        const value = pad.unlinked?.value;
        if (value && value.kind !== 'material') {
          throw new Error('Expected a material');
        }

        const inventory = getInventory();
        const set = (
          material: {
            name: string;
            concentration?: Concentration;
          },
          type: string,
        ) => {
          const value = pad.unlinked?.value;
          if (value && value.kind !== 'material') {
            throw new Error('Expected a material');
          }
          // TODO(april): handle volumes as an alternative
          let concentration: ConcentrationValue;
          if ('concentration' in material && material.concentration?.number) {
            concentration = {
              kind: 'concentration',
              number: material.concentration.number,
              unit: canonicalizeUnit(material.concentration.unit),
            };
          } else {
            concentration = {
              kind: 'concentration',
              number: 1,
              unit: 'x',
            };
          }
          const newValue: MaterialValue = {
            kind: 'material',
            id: 'uncalculated' as unknown as MaterialId,
            label: material.name,
            amount: concentration,
            contents: [],
            tags: {},
          };
          newValue.id = hashMaterial(newValue);
          fireAndForget(this.graph.setValue(pad, newValue));
        };
        const items: Menu[] = (
          [
            {
              kind: 'menu',
              label: 'Buffers',
              items: inventory.buffers.map(b => ({
                kind: 'menu_item',
                label: b.name,
                action: () => set(b, 'buffer'),
              })),
            },
            {
              kind: 'menu',
              label: 'DNA Part',
              items: inventory.dnaParts.map(e => ({
                kind: 'menu_item',
                label: e.name,
                action: () => set(e, 'dnaPart'),
              })),
            },
            {
              kind: 'menu',
              label: 'Enzymes',
              items: inventory.enzymes.map(e => ({
                kind: 'menu_item',
                label: e.name,
                action: () => set(e, 'enzyme'),
              })),
            },
            {
              kind: 'menu',
              label: 'Libraries',
              items: inventory.libraries.map(l => ({
                kind: 'menu_item',
                label: l.name,
                action: () => set(l, 'library'),
              })),
            },
            {
              kind: 'menu',
              label: 'Oligos',
              items: inventory.oligos.map(o => ({
                kind: 'menu_item',
                label: o.name,
                action: () => set(o, 'oligo'),
              })),
            },
            {
              kind: 'menu',
              label: 'Reagents',
              items: inventory.reagents.map(r => ({
                kind: 'menu_item',
                label: r.name,
                action: () => set(r, 'reagent'),
              })),
            },
          ] as Menu[]
        ).filter(menu => menu.items.length > 0);

        let accent: number[];
        if (value?.contents.length === 1) {
          const content = value.contents[0];
          accent = [];
          for (const item of items) {
            const candidate = findPath([item.label, content.label], items);
            if (candidate.length > accent.length) {
              accent = candidate;
            }
          }
        } else {
          accent = [];
        }

        this.openDropdown(items, accent, position, bound);
      },
    });
    this.glRenderer = new WebGLRenderer({alpha: true, antialias: false});
    this.registerDisposer(() => {
      this.glRenderer.dispose();
      this.glRenderer.domElement.remove();
    });
    this.glRenderer.setAnimationLoop(() => {
      this.render();
    });
    this.glRenderer.setSize(1, 1); // make it tiny so we calculate page size correctly
    this.glRenderer.setPixelRatio(window.devicePixelRatio * 2);
    this.root.appendChild(this.glRenderer.domElement);
    this.camera = new OrthographicCamera(0, 0, 0, 0, 0, 10000);
    this.camera.position.z = 10000;

    this.raycaster = new Raycaster();

    this.pointerInterpreter = new PointerInterpreter(this.root, this);
    this.registerListener(this.glRenderer.domElement, 'pointerdown', e => {
      this.pointerInterpreter.down(e);
      this.scene.rerender = true;
    });
    this.registerListener(this.glRenderer.domElement, 'pointermove', e => {
      this.pointerInterpreter.move(e);
      this.scene.rerender = true;
    });
    this.registerListener(this.glRenderer.domElement, 'pointerup', e => {
      this.pointerInterpreter.up(e);
      this.scene.rerender = true;
    });
    this.registerListener(this.glRenderer.domElement, 'wheel', e => {
      this.pointerInterpreter.wheel(e);
      this.scene.rerender = true;
    });
    this.registerListener(this.glRenderer.domElement, 'contextmenu', e => {
      e.preventDefault();
    });
    this.registerListener(window, 'resize', () => {
      this.resize();
      this.scene.rerender = true;
    });
    this.resize();
  }

  down(
    position: Vector2,
    {
      contextual,
      metaKey,
      shiftKey,
    }: {contextual: boolean; metaKey: boolean; shiftKey: boolean},
  ): void {
    if (contextual) {
      return;
    }

    const target = this.findTarget(position);
    const maybeNode = this.findMatchingTarget(position, TYPE_NODE);

    if (target !== this.scene.focused) {
      if (this.scene.focused) {
        this.scene.focused = undefined;
        this.updateState({
          ...this.state,
          input: undefined,
        });
      }
    }

    if (target?.kind === TYPE_PAD) {
      const position3 = target.objects.display.getWorldPosition(new Vector3());
      this.pendingLink = {
        line: new Spline({
          fillStart: (target.objects.display.fill | (0 << 8) | 0xff) as RgbaU32,
          fillEnd: (target.objects.display.fill | (0 << 8) | 0xff) as RgbaU32,
          lineWidth: 2,
          positionStart: position3,
          positionEnd: position3,
          z: Z_LINK,
        }),
        start: target,
        point: position3,
      };
      this.scene.scene.add(this.pendingLink.line);
    } else if (maybeNode) {
      if (metaKey || shiftKey) {
        this.scene.selectAdditive(maybeNode);
      } else {
        this.scene.selectOnly(maybeNode);
      }
    } else if (metaKey || shiftKey) {
      if (!shiftKey) {
        this.scene.clearSelection();
      }
      const geometry = new LineGeometry();
      const vertices = new Float32Array(5 * 3);
      this.pendingDragSelect = {
        boxGeometry: geometry,
        boxVertices: vertices,
        boxObject: new Line2(
          geometry,
          new LineMaterial({
            color: new Color(0x808080),
            dashed: true,
            dashSize: 8,
            gapSize: 8,
            linewidth: 1,
          }),
        ),
        start: this.calculatePosition3(position),
      };
      this.pendingDragSelect.boxObject.position.z = Z_HOVER;
      this.scene.scene.add(this.pendingDragSelect.boxObject);
      // Set up the box geometry
      this.dragPendingSelect(position);
    } else {
      this.scene.clearSelection();
    }
  }

  click(
    position: Vector2,
    px: [x: number, y: number],
    {
      contextual,
      metaKey,
      shiftKey,
      clickCount,
    }: {
      contextual: boolean;
      metaKey: boolean;
      shiftKey: boolean;
      clickCount: number;
    },
  ): void {
    this.release(position, px);

    if (contextual) {
      const items = [...this.menuItems];

      const target = this.findMatchingTarget(position, TYPE_NODE);
      if (target) {
        if (metaKey || shiftKey) {
          this.scene.selectAdditive(target);
        } else {
          this.scene.selectOnly(target);
        }

        items.push({kind: 'divider'});
        items.push({
          kind: 'menu_item',
          label: 'Debug',
          action: () => {
            this.dialog.display(makeNodeDetail(this.graph, target.node)).then(
              () => {},
              () => {},
            );
          },
        });
        items.push({
          kind: 'menu_item',
          label: 'Delete',
          action: () => {
            fireAndForget(
              Promise.all(
                [...this.scene.selected].map(n =>
                  this.graph.deleteNode(n.node),
                ),
              ),
            );
          },
        });
        items.push({
          kind: 'menu_item',
          label: 'Edit',
          action: () => {
            this.openNodeEditor(target.node);
          },
        });
      }

      this.menu.open(
        {
          controller: this,
          graph: this.graph,
          position: this.calculatePosition3(position),
        },
        items,
        new Vector2(px[0], px[1]),
        this.root,
      );
    } else {
      const target = this.findTarget(position);
      if (target && 'onClick' in target) {
        fireAndForget(target.onClick(clickCount));
      }
    }
  }

  async drag(position: Vector2, dx: number, dy: number): Promise<void> {
    this.scene.focused = undefined;

    if (this.pendingDragSelect) {
      this.dragPendingSelect(position);
    } else if (this.pendingLink) {
      this.dragPendingLink(position);
    } else if (this.scene.selected.size > 0) {
      await this.dragSelected(dx, dy);
    } else {
      this.dragCamera(dx, dy);
    }

    if (this.state.padInfo) {
      this.updateState({
        ...this.state,
        padInfo: undefined,
      });
    }
  }

  private dragPendingLink(position: Vector2): void {
    const pending = checkExists(this.pendingLink);
    const start = pending.start;
    const startIsOutput = start.pad.direction === 'output';
    const target = this.findTarget(position, {links: false});
    let aimless;
    let end;
    let fillEnd;
    if (target?.kind === 'pad' && arePadsCompatible(start.pad, target.pad)) {
      aimless = false;
      end = target.objects.display.getWorldPosition(new Vector3());
      fillEnd = (target.objects.display.fill | (0 << 8) | 0xff) as RgbaU32;
    } else {
      aimless = true;
      end = this.calculatePosition3(position);
      fillEnd = 0xfafafaff as RgbaU32;
    }

    const classList = this.glRenderer.domElement.classList;
    if (aimless && !classList.contains(AIMLESS_LINK_CLASS)) {
      classList.add(AIMLESS_LINK_CLASS);
    } else if (!aimless && classList.contains(AIMLESS_LINK_CLASS)) {
      classList.remove(AIMLESS_LINK_CLASS);
    }

    pending.line.setEndpoints(
      startIsOutput ? pending.point : end,
      startIsOutput ? end : pending.point,
    );
    if (startIsOutput) {
      pending.line.updateFill(pending.line.fillStart, fillEnd);
    } else {
      pending.line.updateFill(fillEnd, pending.line.fillEnd);
    }
  }

  private dragPendingSelect(position: Vector2): void {
    const pending = checkExists(this.pendingDragSelect);
    const start = pending.start;
    const end = this.calculatePosition3(position);
    pending.boxVertices.set([
      start.x,
      start.y,
      0,
      start.x,
      end.y,
      0,
      end.x,
      end.y,
      0,
      end.x,
      start.y,
      0,
      start.x,
      start.y,
      0,
    ]);
    pending.boxGeometry.setPositions(pending.boxVertices);
    pending.boxObject.computeLineDistances();
  }

  private async dragSelected(dx: number, dy: number): Promise<void> {
    for (const node of this.scene.selected) {
      const destination = node.objects.group.position.clone();
      const oneOverZoom = 1 / Math.pow(2, this._zoom - 1);
      destination.x += dx * oneOverZoom;
      destination.y += dy * oneOverZoom;
      await this.graph.moveNode(node.node, destination);
    }
  }

  private dragCamera(dx: number, dy: number): void {
    const oneOverZoom = 1 / Math.pow(2, this._zoom - 1);
    this.camera.position.x -= dx * oneOverZoom;
    this.camera.position.y -= dy * oneOverZoom;
  }

  hover(position: Vector2, px: [x: number, y: number]): void {
    const target = this.findTarget(position);
    if (target !== this.scene.hovered) {
      if (this.scene.hovered && 'onLeave' in this.scene.hovered) {
        this.scene.hovered.onLeave();
      }
      if (target && 'onLeave' in target) {
        target.onEnter();
      }
    }
    this.scene.hovered = target;

    if (target?.kind === TYPE_PAD && this.state.padInfo?.target !== target) {
      const content = makePadTooltip(this.graph, target.pad);
      this.updateState({
        ...this.state,
        padInfo: {
          target,
          position: new Vector2(px[0], px[1]),
          content,
        },
      });
    } else if (target?.kind !== TYPE_PAD && this.state.padInfo) {
      this.updateState({
        ...this.state,
        padInfo: undefined,
      });
    }
  }

  release(position: Vector2, px: [x: number, y: number]): void {
    if (this.pendingDragSelect) {
      this.releasePendingSelect(position);
    } else if (this.pendingLink) {
      this.releasePendingLink(position, px);
    }
  }

  private releasePendingLink(
    position: Vector2,
    px: [x: number, y: number],
  ): void {
    const pending = checkExists(this.pendingLink);
    const start = pending.start;
    const target = this.findTarget(position, {links: false});
    if (target?.kind === 'pad' && arePadsCompatible(start.pad, target.pad)) {
      const [output, input] = orderPads(start, target);
      fireAndForget(
        this.graph
          .addLink(output.pad, input.pad)
          .then(() => this.cascadeValues()),
      );
    } else {
      const key = start.pad.direction === 'output' ? 'inputs' : 'outputs';
      const target = this.calculatePosition3(position);
      const padsByNodes = NODE_SPECS.map(
        n =>
          [
            n,
            n[key].filter(spec => isPadAndSpecCompatible(start.pad, spec)),
          ] as const,
      ).filter(([_node, specs]) => specs.length > 0);
      this.menu.open(
        {
          controller: this,
          graph: this.graph,
          position: target,
        },
        padsByNodes.map(([nodeSpec, specs]) => ({
          kind: 'menu',
          label: nodeSpec.label,
          items: specs.map(spec => ({
            kind: 'menu_item',
            label: spec.label,
            action: async () => {
              const node = await this.graph.addNode(target, nodeSpec);
              const pad = checkExists(node[key].get(spec.id));
              const output = start.pad.direction === 'output' ? start.pad : pad;
              const input = start.pad.direction === 'output' ? pad : start.pad;
              await this.graph.addLink(
                output as Pad<'output'>,
                input as Pad<'input'>,
              );
              await this.cascadeValues();
            },
          })),
        })),
        new Vector2(px[0], px[1]),
        this.root,
      );
    }

    removeAndDispose(pending.line);
    this.glRenderer.domElement.classList.remove(AIMLESS_LINK_CLASS);
    this.pendingLink = undefined;
  }

  private releasePendingSelect(position: Vector2): void {
    const pending = checkExists(this.pendingDragSelect);
    const box = new Box3().setFromPoints([
      pending.start,
      this.calculatePosition3(position),
    ]);
    box.min.z = Number.MIN_VALUE;
    box.max.z = Number.MAX_VALUE;
    for (const node of this.scene.intersectNodes(box)) {
      this.scene.selectAdditive(node);
    }
    removeAndDispose(pending.boxObject);
    this.pendingDragSelect = undefined;
  }

  zoom(amount: number, position: Vector2): void {
    const scale = Math.pow(2, amount) - 1;
    this.camera.position.x += scale * position.x * this.camera.right;
    this.camera.position.y += scale * position.y * this.camera.top;
    this._zoom += amount;
    this.resize();
  }

  addSubgraph(position: Vector3): void {
    this.dialog
      .display(makeAddSubgraphDialog({graph: this.graph, position}))
      .then(
        () => {},
        () => {},
      );
  }

  async simulateRun(): Promise<void> {
    // When the dialog closes by clickout the promise is rejected, but that's fine
    const ignore = () => {};
    this.dialog
      .display(makeSimulateScriptDialog({graph: this.graph}))
      .then(ignore, ignore);
  }

  async inputChanged(e: CorgiEvent<typeof CHANGED>): Promise<void> {
    const focused = this.scene.focused;
    if (!focused) {
      return;
    }

    if (!('onSave' in focused)) {
      return;
    }

    await focused.onSave(e.detail.value);

    const bound = checkExists(focused.background.geometry.boundingBox);
    await this.updateState({
      ...this.state,
      input: this.state.input
        ? {
            ...this.state.input,
            size: new Vector2(
              bound.max.x - bound.min.x,
              bound.max.y - bound.min.y,
            ),
            // TODO(april): corgi has major bugs around input handling if the user is typing fast. It's
            // hard to debug so in the meantime we hack around it.
            value: undefined,
          }
        : undefined,
    });
  }

  getCamera(): CameraState {
    return {
      version: 1,
      x: this.camera.position.x,
      y: this.camera.position.y,
      zoom: this._zoom,
    };
  }

  setCamera(camera: CameraState): void {
    if (camera.version === 1) {
      this.camera.position.x = camera.x;
      this.camera.position.y = camera.y;
      this._zoom = camera.zoom;
      this.resize(); // update the camera bounds
    }
  }

  private async cascadeValues(): Promise<void> {
    let attempts = 0;
    let error;
    while (attempts < 3) {
      try {
        await settle(this.graph, new NoOpActionsHandler(), /* sandbox= */ true);
        break;
      } catch (e: unknown) {
        error = e;
      }

      attempts += 1;
    }

    if (error) {
      console.error('Cascading values failed multiple times', error);
      // We need some setValue calls to succeed so that the tables updates with valid columns. There
      // may be another graph running by now, so yikes, but it's not super clear what else we can
      // do.
      // TODO(april) I guess we could make settling never fail with an exception? And then return
      // overlay from it? And run commit from it if it's still the same overlay by the time we get
      // here? Idk if we're really solving a real problem.
      await this.graph.overlay?.commit();
    }

    const fullGraphUpdate: Patch[] = [...this.graph.nodes.values()].map(
      node => ({
        kind: 'update_node',
        node,
      }),
    );
    await this.scene.patch(fullGraphUpdate);
  }

  private openDropdown(
    items: Array<Menu | MenuItem>,
    accent: number[],
    position: Vector3,
    bound: Box3,
  ): void {
    const screen = this.calculatePx(position);
    screen.x += bound.min.x;
    screen.y += bound.max.y;
    this.menu.open(
      {
        controller: this,
        graph: this.graph,
        position: position,
      },
      items,
      screen,
      this.root,
      {
        accent,
        anchor: 'top_left',
      },
    );
  }

  private openNodeEditor(node: Node): void {
    const draft = {node};
    const onUpdate = (node: Node) => {
      draft.node = node;
    };
    const save = () => this.graph.patchNode(draft.node);
    this.dialog
      .display(makeNodeEditor({node: draft.node, onUpdate}))
      // When the dialog closes by clickout the promise is rejected, but that's fine
      .then(save, save);
  }

  private calculatePosition3(position: Vector2): Vector3 {
    this.raycaster.setFromCamera(position, this.camera);
    const target = new Vector3();
    return checkExists(this.raycaster.ray.intersectPlane(CLICK_PLANE, target));
  }

  private calculatePx(vector: Vector3): Vector2 {
    vector.project(this.camera);
    const viewport = this.glRenderer.domElement.getBoundingClientRect();
    return new Vector2(
      (0.5 + vector.x / 2) * viewport.width,
      (0.5 - vector.y / 2) * viewport.height,
    );
  }

  private findTarget(
    position: Vector2,
    include?: {links?: boolean},
  ): ObjectHandle | undefined {
    this.raycaster.setFromCamera(position, this.camera);
    const objects = this.raycaster.intersectObjects(this.scene.scene.children);
    // ThreeJS sorts objects in intersectObjects, but we only want to sort on the Z-axis so we redo
    // it.
    objects.sort((a, b) => b.object.position.z - a.object.position.z);

    let target;
    for (const root of objects) {
      const object = root.object;

      if (!root.object.visible) {
        continue;
      }
      if (root.object.name === TYPE_IGNORE) {
        continue;
      }
      if (include?.links === false && root.object.name === TYPE_LINK) {
        continue;
      }

      let cursor: Object3D | null = object;
      while (cursor && !this.scene.query(cursor)) {
        cursor = cursor.parent;
      }

      if (cursor) {
        target = this.scene.query(cursor);
        break;
      }
    }
    return target;
  }

  private findMatchingTarget<K extends ObjectHandle['kind']>(
    position: Vector2,
    type: K,
  ): TypedObjectHandle<K> | undefined {
    this.raycaster.setFromCamera(position, this.camera);
    const objects = this.raycaster.intersectObjects(this.scene.scene.children);

    let target;
    for (const root of objects) {
      const object = root.object;

      if (root.object.name === TYPE_IGNORE) {
        continue;
      }

      let cursor: Object3D | null = object;
      while (cursor && this.scene.query(cursor)?.kind !== type) {
        cursor = cursor.parent;
      }

      if (cursor) {
        target = this.scene.query(cursor);
        break;
      }
    }
    return target as TypedObjectHandle<K> | undefined;
  }

  private render(): void {
    if (this.scene.rerender) {
      this.glRenderer.render(this.scene.scene, this.camera);
      this.scene.rerender = false;
    }
  }

  private resize(): void {
    const rect = this.root.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    this.glRenderer.setSize(width, height);
    this.pointerInterpreter.resize(rect);

    const oneOverZoom = 1 / Math.pow(2, this._zoom);
    this.camera.left = -width * oneOverZoom;
    this.camera.right = width * oneOverZoom;
    this.camera.top = height * oneOverZoom;
    this.camera.bottom = -height * oneOverZoom;
    this.camera.updateProjectionMatrix();
  }
}

function findPath(
  path: string[],
  items: Array<Divider | Menu | MenuItem>,
): number[] {
  for (let i = 0; i < items.length; ++i) {
    const item = items[i];
    if (item.kind === 'divider') {
      return [i];
    }

    if (path[0] === item.label) {
      if (item.kind === 'menu') {
        return [i, ...findPath(path.slice(1), item.items)];
      } else if (item.kind === 'menu_item') {
        return [i];
      } else {
        throw checkExhaustive(item);
      }
    }
  }
  return [];
}

function optionsToFlatMenu(
  nullable: boolean,
  ids: readonly string[],
  labels: readonly string[],
  value: string | undefined,
  callback: (value: string | undefined) => void,
): {
  accent: number[];
  items: MenuItem[];
} {
  checkArgument(ids.length === labels.length);
  const items: MenuItem[] = [];
  if (nullable) {
    items.push({
      kind: 'menu_item',
      label: '(unset)',
      action: () => {
        callback(undefined);
      },
    });
  }
  items.push(
    ...ids.map(
      (_, i) =>
        ({
          kind: 'menu_item',
          label: labels[i],
          action: () => {
            callback(ids[i]);
          },
        }) as const,
    ),
  );

  let selected = -1;
  if (value) {
    selected = ids.indexOf(value);
  }
  if (nullable) {
    selected += 1;
  } else if (selected < 0) {
    selected = 0;
  }

  return {
    accent: [selected],
    items,
  };
}

function orderPads(
  a: PadHandle<'input' | 'output'>,
  b: PadHandle<'output' | 'input'>,
): [PadHandle<'output'>, PadHandle<'input'>] {
  if (isOutput(a) && isInput(b)) {
    return [a, b];
  } else if (isInput(a) && isOutput(b)) {
    return [b, a];
  } else {
    throw new Error(`Pads are both ${a.pad.direction}s`);
  }
}

function isInput(
  handle: PadHandle<'input' | 'output'>,
): handle is PadHandle<'input'> {
  return handle.pad.direction === 'input';
}

function isOutput(
  handle: PadHandle<'input' | 'output'>,
): handle is PadHandle<'output'> {
  return handle.pad.direction === 'output';
}
